/**
 * ChangePassword — смена пароля команды. После входа по временному паролю
 * сюда ведёт сам вход (forced): без своего пароля в панель не идём. Иначе —
 * из меню команды в сайдбаре, с кнопкой «Назад».
 */

import {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';

import {changePassword} from '@services/auth';
import {HttpError} from '@services/utils';
import {Field, TextInput} from '@components/FormKit';
import {Button} from '@components/ui/Button';

const MIN = 8;

const errorText = (err: unknown): string =>
  err instanceof HttpError && (err.status === 400 || err.status === 401 || err.status === 422)
    ? 'Текущий пароль не подошёл'
    : 'Не удалось сменить пароль. Попробуйте ещё раз';

export const ChangePassword = () => {
  const navigate = useNavigate();
  const forced = (useLocation().state as {forced?: boolean} | null)?.forced === true;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => navigate('/dashboard', {replace: true}),
  });

  const hint =
    next.length > 0 && next.length < MIN
      ? `Минимум ${MIN} знаков`
      : repeat.length > 0 && repeat !== next
        ? 'Пароли не совпадают'
        : next !== '' && next === current
          ? 'Новый пароль совпадает с текущим'
          : null;
  const valid = current !== '' && next.length >= MIN && repeat === next && next !== current;
  const handleSubmit = () => mutation.mutate({currentPassword: current, newPassword: next});

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-[372px]">
        <h1 className="text-[24px] font-bold -tracking-[.02em]">
          {forced ? 'Придумайте свой пароль' : 'Сменить пароль'}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.45] text-muted-foreground">
          {forced
            ? 'Вы вошли по временному паролю. Задайте постоянный — дальше входите с ним.'
            : 'Новый пароль действует со следующего входа.'}
        </p>
        <div className="mt-7 flex flex-col gap-3.5">
          <Field label={forced ? 'Временный пароль' : 'Текущий пароль'}>
            <TextInput
              type="password"
              value={current}
              autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label={`Новый пароль (от ${MIN} знаков)`}>
            <TextInput
              type="password"
              value={next}
              autoComplete="new-password"
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label="Ещё раз">
            <TextInput
              type="password"
              value={repeat}
              autoComplete="new-password"
              onChange={(e) => setRepeat(e.target.value)}
            />
          </Field>
          {(hint !== null || mutation.isError) && (
            <div className="rounded-[10px] bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
              {hint ?? errorText(mutation.error)}
            </div>
          )}
          <Button
            className="h-12 rounded-[10px] text-[15px]"
            disabled={!valid || mutation.isPending}
            onClick={handleSubmit}
          >
            {mutation.isPending ? 'Сохраняем…' : 'Сохранить пароль'}
          </Button>
          {!forced && (
            <Link
              to="/dashboard"
              className="self-center text-[13px] text-muted-foreground hover:text-primary"
            >
              Назад в панель
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
